import { useMemo } from 'react';

import { DataTable, SortableHeader } from '@/components/ui/data-table';

import { useKnowledgeBaseDetailQuery } from '@/query/knowledge.query';

import { DATE_FORMAT, formatDate } from '@/lib/date';
import { getDocumentIcon } from '@/lib/document';
import { getFileName } from '@/lib/utils';

import { DocumentEmptyState } from './document-empty-state';
import { DocumentRowActions } from './document-row-actions';
import { DocumentSearchFilter } from './document-search-filter';

import { useDocumentsTableStore } from '@/store/documents-table.store';
import { useKnowledgeModalsStore } from '@/store/knowledge-modals.store';
import type { KnowledgeBaseFile } from '@/types/knowledge.type';
import { type ColumnDef } from '@tanstack/react-table';

// ---------------------------------------------------------------------------
// DocumentsList
// ---------------------------------------------------------------------------
export function DocumentsList() {
  const openDocumentDetails = useKnowledgeModalsStore(state => state.openDocumentDetails);
  const selectedKnowledgeBase = useKnowledgeModalsStore(state => state.selectedKnowledgeBase);
  const selectedDocument = useKnowledgeModalsStore(state => state.selectedDocument);

  const { filters, query, sort, setSort } = useDocumentsTableStore();
  const typeFilter = filters.type;

  const { data: kbDetail, isLoading } = useKnowledgeBaseDetailQuery(
    selectedKnowledgeBase?.kb_id || 0,
    { enabled: !!selectedKnowledgeBase }
  );

  const allDocuments = kbDetail?.file_list || [];

  const hasActiveFilters = query !== '' || typeFilter !== 'all';

  const filteredDocuments = useMemo(() => {
    let docs = allDocuments;

    if (query) {
      const lowerQuery = query.toLowerCase();
      docs = docs.filter(
        doc =>
          getFileName(doc.file_path).toLowerCase().includes(lowerQuery) ||
          doc.file_path.toLowerCase().includes(lowerQuery)
      );
    }

    if (typeFilter !== 'all') {
      docs = docs.filter(doc => doc.extension.toLowerCase() === typeFilter.toLowerCase());
    }

    return docs;
  }, [allDocuments, query, typeFilter]);

  // ---- column definitions ----
  const columns = useMemo<ColumnDef<KnowledgeBaseFile>[]>(
    () => [
      {
        id: 'file_path',
        accessorFn: row => getFileName(row.file_path),
        header: ({ column }) => <SortableHeader label="File Name" column={column} />,
        cell: ({ row }) => {
          const doc = row.original;
          const fileName = getFileName(doc.file_path);
          return (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">{getDocumentIcon(doc.extension)}</div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-foreground truncate">{fileName}</div>
                <div className="text-xs text-muted-foreground truncate">{doc.file_path}</div>
              </div>
            </div>
          );
        },
      },
      {
        id: 'type',
        accessorKey: 'extension',
        header: 'Extension',
        cell: ({ getValue }) => (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-muted">
            {getValue() as string}
          </span>
        ),
        size: 100,
      },
      {
        id: 'updated_at',
        accessorKey: 'updated_at',
        header: 'Updated at',
        cell: ({ getValue }) => (
          <span className="text-sm text-foreground">
            {formatDate(getValue() as string, DATE_FORMAT.SHORT)}
          </span>
        ),
        size: 140,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 48,
        cell: ({ row }) => (
          <DocumentRowActions
            knowledgeBaseID={selectedKnowledgeBase.kb_id}
            document={row.original}
          />
        ),
      },
    ],
    [selectedKnowledgeBase, selectedDocument]
  );

  return (
    <div className="p-4">
      <div className="space-y-4">
        <DocumentSearchFilter />

        <div className="overflow-hidden rounded-lg border">
          <DataTable
            columns={columns}
            data={filteredDocuments}
            sorting={sort}
            isLoading={isLoading}
            onSortingChange={setSort}
            onRowClick={doc => openDocumentDetails(doc.file_path)}
            emptyState={<DocumentEmptyState hasActiveFilters={hasActiveFilters} />}
          />
          {/* <DocumentPagination totalItems={totalItems} totalPages={totalPages} /> */}
        </div>
      </div>
    </div>
  );
}
